import React from 'react';

/**
 * Provider component type definition
 */
type ProviderComponent = React.ComponentType<{ children: React.ReactNode }>;

/**
 * Composes multiple React providers into a single component
 *
 * This utility eliminates provider nesting hell by combining multiple
 * context providers into a single composed provider.
 *
 * @example
 * ```tsx
 * const AppProviders = composeProviders(
 *   ThemeProvider,
 *   AuthProvider,
 *   DataProvider
 * );
 *
 * // Usage
 * <AppProviders>
 *   <App />
 * </AppProviders>
 * ```
 *
 * @param providers - Array of provider components to compose
 * @returns A single composed provider component
 */
export function composeProviders(...providers: ProviderComponent[]): ProviderComponent {
  return providers.reduce<ProviderComponent>(
    (AccumulatedProviders, CurrentProvider) => {
      return ({ children }) => (
        <AccumulatedProviders>
          <CurrentProvider>{children}</CurrentProvider>
        </AccumulatedProviders>
      );
    },
    ({ children }) => <>{children}</>,
  );
}
